import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inboxService, type InboxReplyDto, type InboxListParams } from '@/shared/services/inbox.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

const INBOX_KEY = 'inbox';

import { INBOX_POLL_MS } from '@/shared/lib/inbox-poll';

export { INBOX_POLL_MS };

export function useInboxMessages(params?: InboxListParams, options: { enabled?: boolean; poll?: boolean } = {}) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [INBOX_KEY, params, wsId],
    queryFn: () => inboxService.list(params),
    enabled: !!wsId && (options.enabled ?? true),
    ...(options.poll ? { refetchInterval: INBOX_POLL_MS, refetchIntervalInBackground: false } : {}),
  });
}

/**
 * The inbox list a page at a time, newest first, with the filters applied
 * by the server (search, platform, a date range), so nothing older than the
 * first page is out of reach. Polls while the tab is visible.
 */
export function useInboxPages(params: Omit<InboxListParams, 'page'>) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useInfiniteQuery({
    queryKey: [INBOX_KEY, 'pages', params, wsId],
    queryFn: ({ pageParam }) => inboxService.list({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.pagination.hasMore ? last.pagination.page + 1 : undefined),
    enabled: !!wsId,
    refetchInterval: INBOX_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useInboxMessage(id: string | undefined) {
  return useQuery({
    queryKey: [INBOX_KEY, id],
    queryFn: () => inboxService.getById(id!),
    enabled: !!id,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inboxService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_KEY] });
    },
  });
}

export function useToggleStar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inboxService.toggleStar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_KEY] });
    },
  });
}

/** Read, star and archive act on every message of a conversation, by its key. */
export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => inboxService.markConversationRead(key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INBOX_KEY] }),
  });
}

export function useStarConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; starred: boolean }) => inboxService.starConversation(v.key, v.starred),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INBOX_KEY] }),
  });
}

export function useArchiveConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => inboxService.archiveConversation(key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [INBOX_KEY] }),
  });
}

export function useArchiveMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inboxService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_KEY] });
    },
  });
}

export function useReplyToMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: InboxReplyDto) => inboxService.reply(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_KEY] });
    },
  });
}

export function useSyncInboxNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => inboxService.syncNow(),
    onSuccess: () => {
      // Sync runs in the background — invalidate so the UI re-fetches
      // a few seconds later when worker jobs have written rows.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [INBOX_KEY] });
      }, 4_000);
    },
  });
}

/**
 * Per-day unreplied counts for a date range (the calendar's inbox-load
 * layer). Disabled until the caller has a range and the layer is switched on.
 */
export function useInboxDailyLoad(range: { from: Date; to: Date } | null, enabled = true) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  const from = range?.from.toISOString();
  const to = range?.to.toISOString();
  return useQuery({
    queryKey: [INBOX_KEY, 'daily-load', from, to, tz, wsId],
    queryFn: () => inboxService.dailyLoad({ from: from!, to: to!, tz }),
    enabled: !!wsId && enabled && !!from && !!to,
    staleTime: 60_000,
  });
}

export function useInboxDiagnostics() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [INBOX_KEY, 'diagnostics', wsId],
    queryFn: () => inboxService.diagnostics(),
    enabled: !!wsId,
    // Fired in parallel with the inbox list so the empty state can
    // render immediately when the list comes back empty. Server caches
    // for 60s, so the cost of always-on is one Redis hit per minute.
    staleTime: 60_000,
  });
}
